import React from 'react';
import {
  Typography,
  Button,
  InputBase,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Customer } from './app';
import FormControlError from '~/_base/components/formControlError';
import { useForm } from 'react-hook-form';

const VALID_EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface StepOneProps {
  onNext: (data: Partial<Customer>) => void;
  formState: Partial<Customer>;
  badDomains: string[];
}

export default function StepOne({
  onNext,
  formState,
  badDomains,
}: StepOneProps) {
  const { register, handleSubmit, formState: { errors } } = useForm();

  // Get querystring parameters
  // Ex: http://app.ape.fyi/trialsetup?email=john.smith@agl.com.au&company=agl
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get('email');
  const company = urlParams.get('company');

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <Typography variant="h1">Start your free trial</Typography>
      <Typography variant="body2" style={{ margin: '24px 0' }}>
        To begin your trial, we need your work email and company name
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FormControl
          variant="standard"
          required
          fullWidth
          error={errors.email != null}
          style={{ marginBottom: 16 }}>
          {errors.email && (
            <FormControlError
              message={
                (errors.email?.message as string) || 'Should be your company email address'
              }
            />
          )}
          <InputLabel htmlFor="email">Email</InputLabel>
          <InputBase
            defaultValue={formState.email || email}
            {...register('email', {
              validate: val => {
                const invalidPattern = VALID_EMAIL_REGEX.test(val) === false;
                const atSymbolPos = val.indexOf('@');
                const domain = val.substr(atSymbolPos + 1, val.length);
                const isBadDomain = badDomains.some(d => domain === d);
                return !isBadDomain && !invalidPattern;
              },
              required: 'Email is required',
            })}
            type="email"
            id="email"
            data-autopilot="Email"
            placeholder="Your work email address"
          />
        </FormControl>
        <FormControl
          variant="standard"
          required
          fullWidth
          error={errors.organisation_name != null}>
          {errors.organisation_name && (
            <FormControlError
              message={
                errors.organisation_name.type === 'maxLength'
                  ? 'Must be less than 50 characters'
                  : 'Company Name is required'
              }
            />
          )}
          <InputLabel htmlFor="company">Company name</InputLabel>
          <InputBase
            id="company"
            {...register('organisation_name', {
              validate: val => val.trim() !== '',
              required: true,
              maxLength: 50,
            })}
            defaultValue={formState.organisation_name || company}
            type="text"
            data-autopilot="Company"
            placeholder="Your company name"
          />
        </FormControl>
      </div>
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          style={{ width: 120 }}
        >
          Next
        </Button>
      </div>
    </form>
  );
}
